import { useCallback, useEffect, useState } from 'react';
import { DayList } from '../components/DayList';
import { PinPad } from '../components/PinPad';
import {
  Button,
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Heading,
  Loading,
  Row,
  Screen,
  TextField,
  Title,
} from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSession, type Me } from '../lib/session';
import { useIdleLogout } from '../lib/useIdleLogout';
import { LiveCall, StartBulawa, useStartBooked, type Live, type StartedCall } from './CartCall';
import './cart.css';

/**
 * The cart station (§3). Three doors in order:
 *   1. is this browser a registered cart?   → setup code
 *   2. who is standing here?                → name-pick + PIN
 *   3. the cart's day + Start Mulaqaat      → Phases 4 and 5 fill this in
 */

interface Station {
  id: string;
  name: string;
  location_label: string | null;
}

type StationState =
  | { status: 'loading' }
  | { status: 'unregistered'; reason?: string }
  | { status: 'registered'; station: Station }
  | { status: 'error'; message: string };

/** How often the cart re-checks with the server that its session is still alive. */
const WATCHDOG_MS = 15_000;
/**
 * §0.5: "While any cart screen is open: poll state every 2 seconds (knocks,
 * Bulawa declined, waiting list)." This is that poll.
 */
const LIVE_POLL_MS = 2_000;

export default function Cart() {
  const { state, refresh } = useSession();
  const [station, setStation] = useState<StationState>({ status: 'loading' });

  const loadStation = useCallback(async () => {
    try {
      const res = await api<{ registered: boolean; cart?: Station; reason?: string }>('/api/cart/station');
      setStation(
        res.registered && res.cart
          ? { status: 'registered', station: res.cart }
          : { status: 'unregistered', reason: res.reason }
      );
    } catch (err) {
      setStation({
        status: 'error',
        message: err instanceof ApiError ? err.message : 'Could not reach the shop system.',
      });
    }
  }, []);

  useEffect(() => {
    void loadStation();
  }, [loadStation]);

  const signedIn = state.status === 'in' && state.me.device === 'cart';

  const [live, setLive] = useState<Live | null>(null);

  const loadLive = useCallback(async () => {
    try {
      const res = await api<{ live: Live | null }>('/api/calls/live', { userActive: false });
      setLive(res.live);
    } catch {
      // A dropped poll is not worth interrupting anyone for; the next tick retries.
    }
  }, []);

  // The 2-second heartbeat. Deliberately NOT user-active, so polling can never
  // keep an abandoned cart signed in (§0.5, §0.6).
  useEffect(() => {
    if (!signedIn) {
      setLive(null);
      return;
    }
    void loadLive();
    const t = setInterval(() => void loadLive(), LIVE_POLL_MS);
    return () => clearInterval(t);
  }, [signedIn, loadLive]);

  /**
   * A call is running, so the 5-minute idle logout is suspended (§0.5: NEVER
   * mid-call). The server independently refuses to expire a session whose member
   * has a live call, so this holds even if the browser misbehaves.
   */
  const callInProgress = live !== null;

  // Watchdog: notices a session ended elsewhere (idle expiry, admin revoking the
  // cart) without waiting for someone to touch the screen. Deliberately NOT
  // user-active, so polling can never keep an abandoned cart signed in.
  useEffect(() => {
    if (!signedIn) return;
    const t = setInterval(() => void refresh({ userActive: false }), WATCHDOG_MS);
    return () => clearInterval(t);
  }, [signedIn, refresh]);

  const handleIdle = useCallback(async () => {
    try {
      await api('/api/cart/logout', { method: 'POST', body: { reason: 'idle' }, userActive: false });
    } catch {
      // Even if the call fails, the server refuses an idle cart session anyway.
    }
    await refresh({ userActive: false });
  }, [refresh]);

  useIdleLogout({ enabled: signedIn, paused: callInProgress, onIdle: () => void handleIdle() });

  if (station.status === 'loading' || state.status === 'loading') {
    return (
      <Screen>
        <Title sub="Cart station">Mulaqaat</Title>
        <Card>
          <Loading label="Checking this screen…" />
        </Card>
      </Screen>
    );
  }

  if (station.status === 'error') {
    return (
      <Screen>
        <Title sub="Cart station">Mulaqaat</Title>
        <Card>
          <ErrorState message={station.message} onRetry={() => void loadStation()} />
        </Card>
      </Screen>
    );
  }

  if (station.status === 'unregistered') {
    return <RegisterStation reason={station.reason} onDone={() => void loadStation()} />;
  }

  if (!signedIn) {
    return (
      <NamePick
        station={station.station}
        onSignedIn={() => void refresh({ userActive: true })}
      />
    );
  }

  return (
    <CartHome
      station={station.station}
      me={(state as { me: Me }).me}
      live={live}
      refreshLive={loadLive}
      onSignedOut={() => void refresh()}
    />
  );
}

/** Door 1 — the setup code an admin issued for this cart. */
function RegisterStation({ reason, onDone }: { reason?: string; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/cart/register', { method: 'POST', body: { device_token: code.trim() } });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <Title sub="Set up this screen as a cart">Mulaqaat</Title>
      <Card>
        {reason ? <p className="cart__notice">{reason}</p> : null}
        <Heading>Setup code</Heading>
        <p className="cart__lead">
          An admin creates a cart in the Admin screen and shows the code once. Enter it here — this screen
          only needs it once.
        </p>
        <form onSubmit={submit} noValidate>
          <TextField
            label="Setup code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="Paste the code"
          />
          {error ? (
            <p className="field__error" role="alert" style={{ marginBottom: 14 }}>
              {error}
            </p>
          ) : null}
          <Button type="submit" busy={busy} full disabled={code.trim().length === 0}>
            {busy ? 'Setting up…' : 'Set up this cart'}
          </Button>
        </form>
      </Card>
    </Screen>
  );
}

/** Door 2 — tap your name, enter your PIN. */
function NamePick({ station, onSignedIn }: { station: Station; onSignedIn: () => void }) {
  const [staff, setStaff] = useState<{ id: string; name: string }[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await api<{ staff: { id: string; name: string }[] }>('/api/cart/staff');
      setStaff(res.staff);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not load the staff list.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function submit() {
    if (busy || !picked || pin.length < 4) return;
    setBusy(true);
    setError(null);
    try {
      await api('/api/cart/login', { method: 'POST', body: { staff_id: picked.id, pin } });
      setPin('');
      onSignedIn();
    } catch (err) {
      setPin('');
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  if (picked) {
    return (
      <Screen>
        <Title sub={station.name}>Mulaqaat</Title>
        <Card>
          <Heading>{picked.name}</Heading>
          <p className="cart__lead">Enter your PIN.</p>
          <PinPad value={pin} onChange={setPin} disabled={busy} />
          {error ? (
            <p className="field__error cart__center" role="alert">
              {error}
            </p>
          ) : null}
          <Row className="cart__actions">
            <Button
              variant="ghost"
              onClick={() => {
                setPicked(null);
                setPin('');
                setError(null);
              }}
              disabled={busy}
            >
              Not you?
            </Button>
            <Button onClick={() => void submit()} busy={busy} disabled={pin.length < 4}>
              {busy ? 'Checking…' : 'Sign in'}
            </Button>
          </Row>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen width="wide">
      <Title sub={station.location_label ? `${station.name} · ${station.location_label}` : station.name}>
        Mulaqaat
      </Title>
      <Card>
        <Heading>Who is here?</Heading>
        {loadError ? (
          <ErrorState message={loadError} onRetry={() => void load()} />
        ) : !staff ? (
          <Loading label="Loading the team…" />
        ) : staff.length === 0 ? (
          <EmptyState line="No active staff yet. An admin needs to add someone first." />
        ) : (
          <div className="namegrid">
            {staff.map((s) => (
              <button key={s.id} type="button" className="namegrid__item" onClick={() => setPicked(s)}>
                {s.name}
              </button>
            ))}
          </div>
        )}
      </Card>
    </Screen>
  );
}

/** Door 3 — signed in at the cart: the day, starting a call, and being in one. */
function CartHome({
  station,
  me,
  live,
  refreshLive,
  onSignedOut,
}: {
  station: Station;
  me: Me;
  live: Live | null;
  refreshLive: () => Promise<void> | void;
  onSignedOut: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState<StartedCall | null>(null);
  const [share, setShare] = useState<{ link: string; name: string; phone: string } | null>(null);
  const { start: startBooked, busy: startBusy, error: startError } = useStartBooked();

  async function signOut() {
    if (busy) return;
    setBusy(true);
    try {
      await api('/api/cart/logout', { method: 'POST', body: { reason: 'manual' } });
    } finally {
      setBusy(false);
      onSignedOut();
    }
  }

  // A call is running — the cart shows nothing else.
  if (live) {
    return (
      <Screen width="wide">
        <Title sub={`${station.name} · ${me.staff.name}`}>Mulaqaat</Title>
        <LiveCall
          live={live}
          started={started}
          shareLink={share?.link ?? null}
          sharePhone={share?.phone ?? null}
          shareName={share?.name ?? null}
          onEnded={() => {
            setStarted(null);
            setShare(null);
            void refreshLive();
          }}
        />
      </Screen>
    );
  }

  if (starting) {
    return (
      <Screen width="wide">
        <Title sub={`${station.name} · ${me.staff.name}`}>Mulaqaat</Title>
        <StartBulawa
          onCancel={() => setStarting(false)}
          onStarted={(call, token, customer) => {
            setStarted(call);
            setShare({
              link: `${window.location.origin}/b/${token}`,
              name: customer.name,
              phone: customer.phone,
            });
            setStarting(false);
            void refreshLive();
          }}
        />
      </Screen>
    );
  }

  return (
    <Screen width="wide">
      <Title sub={`${station.name} · ${me.staff.name}`}>Mulaqaat</Title>

      <Card>
        <Row>
          <Chip tone="gold">Cart</Chip>
          <Chip tone="live">Signed in</Chip>
        </Row>
      </Card>

      <Card>
        <Heading>This cart&apos;s day</Heading>
        {startError ? (
          <p className="field__error" role="alert">
            {startError}
          </p>
        ) : null}
        <DayList
          cartId={station.id}
          emptyLine="Nothing booked on this cart today."
          showCartName={false}
          onStart={async (bookingId) => {
            const call = await startBooked(bookingId);
            if (call) {
              setStarted(call);
              await refreshLive();
            }
          }}
          startingId={startBusy}
        />
      </Card>

      <Card>
        <Heading>Start a Bulawa</Heading>
        <p className="cart__lead">Call someone now, without an appointment.</p>
        <Button onClick={() => setStarting(true)}>Start a Bulawa</Button>
      </Card>

      <Row className="signout-row">
        <Button variant="secondary" onClick={() => void signOut()} busy={busy}>
          Done — sign out
        </Button>
        <span className="cart__hint">This cart signs out on its own after 5 quiet minutes.</span>
      </Row>
    </Screen>
  );
}
